import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { Wordmark } from '@/components/Logo'
import { ThemeToggle } from '@/components/ThemeToggle'
import { cn } from '@/lib/utils'

/**
 * Chrome for the unauthenticated flows (register, verify, callback errors).
 * Two-column on wide screens so the form is never a lone narrow strip.
 */
export function PublicLayout({
  children,
  aside,
  className,
}: {
  children: ReactNode
  aside?: ReactNode
  className?: string
}) {
  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6 lg:px-8">
          <Link to="/" className="text-foreground">
            <Wordmark className="text-[1.0625rem]" />
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <div className="mx-auto w-full max-w-6xl flex-1 px-6 py-14 lg:px-8 lg:py-20">
        <div
          className={cn(
            aside === undefined
              ? 'mx-auto w-full max-w-2xl'
              : 'grid gap-12 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-16',
            className,
          )}
        >
          <div className="min-w-0">{children}</div>
          {aside === undefined ? null : (
            <aside className="lg:border-l lg:border-border lg:pl-12">{aside}</aside>
          )}
        </div>
      </div>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6 text-sm text-muted-foreground lg:px-8">
          <span>saltseed</span>
          <Link to="/" className="transition-colors hover:text-foreground">
            Back to the overview
          </Link>
        </div>
      </footer>
    </div>
  )
}

/** Small labelled list used in the aside columns. */
export function AsideNotes({
  title,
  items,
}: {
  title: string
  items: ReadonlyArray<{ term: string; detail: ReactNode }>
}) {
  return (
    <div className="space-y-5">
      <p className="text-[0.6875rem] font-medium tracking-[0.12em] text-primary uppercase">
        {title}
      </p>
      <dl className="space-y-4">
        {items.map((item) => (
          <div key={item.term} className="space-y-1">
            <dt className="text-sm font-medium text-foreground">{item.term}</dt>
            <dd className="text-sm leading-relaxed text-pretty text-muted-foreground">
              {item.detail}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
